import type { ExtendedResource } from '@seen/core/ExtendedResource'
import { invariant } from '@seen/core/invariant'
import type { QueryTypes } from '@seen/core/search'
import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

interface OptionsType<T extends ExtendedResource> {
  instance: T
  query: QueryTypes
}

export interface UseCreateIfNoneExistsMutationOptions<
  T extends ExtendedResource,
> {
  mutation?: Omit<UseMutationOptions<T, unknown, OptionsType<T>>, 'mutationFn'>
}

export function useCreateIfNoneExistsMutation<T extends ExtendedResource>(
  options?: UseCreateIfNoneExistsMutationOptions<T>
): UseMutationResult<T, unknown, OptionsType<T>> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const isReadOnly = useIsReadOnly()
  return useMutation({
    ...options?.mutation,
    onSuccess: (data, variables, context) => {
      invariant(data.id, 'Data ID is required')
      const type = variables.instance.fhirType
      cacheKeys.invalidateMutation(queryClient, type, data.id)
      queryClient.setQueryData(
        cacheKeys.read(type, data.id),
        data.toFhirResource()
      )
      const className = data.constructor.name
      queryClient.setQueryData(
        cacheKeys.readExtended(type, data.id, className),
        data
      )
      options?.mutation?.onSuccess?.(data, variables, context)
    },
    mutationFn: async (data) => {
      if (isReadOnly) {
        console.warn(
          '[read-only] mutation attempted: useCreateIfNoneExistsMutation',
          data.instance.fhirType
        )
      }
      return medplum.createResourceClassIfNoneExist(data.instance, data.query)
    },
  })
}
